export const PROFESSOR_TUTORIAL_CLASSROOM_PATH = "/aula";

export const PROFESSOR_TUTORIAL_PHASES = {
  NAV_AULA: "nav-aula",
  CLASSROOM_OVERVIEW: "classroom-overview",
  CREATE_CLASSROOM_BUTTON: "create-classroom-button",
  CLASSROOM_FORM_CENTER: "classroom-form-center",
  CLASSROOM_FORM_NAME: "classroom-form-name",
  CLASSROOM_FORM_COURSE: "classroom-form-course",
  WAIT_CLASSROOM_CREATED: "wait-classroom-created",
  STUDENTS_SECTION: "students-section",
  ADD_STUDENTS: "add-students",
  CSV_TEMPLATE: "csv-template",
  CSV_UPLOAD: "csv-upload",
};

export const PROFESSOR_TUTORIAL_SELECTORS = {
  navAula: '[data-professor-tutorial="classrooms-nav"]',
  classroomOverview: '[data-professor-tutorial="classroom-overview"]',
  createClassroom: '[data-professor-tutorial="create-classroom"]',
  classroomCenter: '[data-professor-tutorial="classroom-center"]',
  classroomName: '[data-professor-tutorial="classroom-name"]',
  classroomCourse: '[data-professor-tutorial="classroom-course"]',
  studentsSection: '[data-professor-tutorial="students-section"]',
  addStudents: '[data-professor-tutorial="add-students"]',
  csvTemplate: '[data-professor-tutorial="csv-template"]',
  csvUpload: '[data-professor-tutorial="csv-upload"]',
};

const NEW_CLASSROOM_PHASE_ORDER = [
  PROFESSOR_TUTORIAL_PHASES.NAV_AULA,
  PROFESSOR_TUTORIAL_PHASES.CLASSROOM_OVERVIEW,
  PROFESSOR_TUTORIAL_PHASES.CREATE_CLASSROOM_BUTTON,
  PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_CENTER,
  PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_NAME,
  PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_COURSE,
  PROFESSOR_TUTORIAL_PHASES.STUDENTS_SECTION,
  PROFESSOR_TUTORIAL_PHASES.ADD_STUDENTS,
  PROFESSOR_TUTORIAL_PHASES.CSV_TEMPLATE,
  PROFESSOR_TUTORIAL_PHASES.CSV_UPLOAD,
];

const EXISTING_CLASSROOM_PHASE_ORDER = [
  PROFESSOR_TUTORIAL_PHASES.NAV_AULA,
  PROFESSOR_TUTORIAL_PHASES.CLASSROOM_OVERVIEW,
  PROFESSOR_TUTORIAL_PHASES.STUDENTS_SECTION,
  PROFESSOR_TUTORIAL_PHASES.ADD_STUDENTS,
  PROFESSOR_TUTORIAL_PHASES.CSV_TEMPLATE,
  PROFESSOR_TUTORIAL_PHASES.CSV_UPLOAD,
];

export function getProfessorTutorialPhaseOrder(hasClassrooms) {
  return hasClassrooms ? EXISTING_CLASSROOM_PHASE_ORDER : NEW_CLASSROOM_PHASE_ORDER;
}

export function getProfessorTutorialVisiblePhase(phase) {
  if (phase === PROFESSOR_TUTORIAL_PHASES.WAIT_CLASSROOM_CREATED) {
    return PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_COURSE;
  }
  return phase;
}

export function getProfessorTutorialStepIndex(phase, hasClassrooms) {
  const visiblePhase = getProfessorTutorialVisiblePhase(phase);
  const index = getProfessorTutorialPhaseOrder(hasClassrooms).indexOf(visiblePhase);
  return index >= 0 ? index : 0;
}

export function getProfessorTutorialSelectorForPhase(phase) {
  switch (getProfessorTutorialVisiblePhase(phase)) {
    case PROFESSOR_TUTORIAL_PHASES.NAV_AULA:
      return PROFESSOR_TUTORIAL_SELECTORS.navAula;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_OVERVIEW:
      return PROFESSOR_TUTORIAL_SELECTORS.classroomOverview;
    case PROFESSOR_TUTORIAL_PHASES.CREATE_CLASSROOM_BUTTON:
      return PROFESSOR_TUTORIAL_SELECTORS.createClassroom;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_CENTER:
      return PROFESSOR_TUTORIAL_SELECTORS.classroomCenter;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_NAME:
      return PROFESSOR_TUTORIAL_SELECTORS.classroomName;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_COURSE:
      return PROFESSOR_TUTORIAL_SELECTORS.classroomCourse;
    case PROFESSOR_TUTORIAL_PHASES.STUDENTS_SECTION:
      return PROFESSOR_TUTORIAL_SELECTORS.studentsSection;
    case PROFESSOR_TUTORIAL_PHASES.ADD_STUDENTS:
      return PROFESSOR_TUTORIAL_SELECTORS.addStudents;
    case PROFESSOR_TUTORIAL_PHASES.CSV_TEMPLATE:
      return PROFESSOR_TUTORIAL_SELECTORS.csvTemplate;
    case PROFESSOR_TUTORIAL_PHASES.CSV_UPLOAD:
      return PROFESSOR_TUTORIAL_SELECTORS.csvUpload;
    default:
      return null;
  }
}

export function getProfessorTutorialNextPhase({ phase, hasClassrooms, classroomCreated = false, importOpen = false }) {
  switch (phase) {
    case PROFESSOR_TUTORIAL_PHASES.NAV_AULA:
      return PROFESSOR_TUTORIAL_PHASES.CLASSROOM_OVERVIEW;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_OVERVIEW:
      return hasClassrooms ? PROFESSOR_TUTORIAL_PHASES.STUDENTS_SECTION : PROFESSOR_TUTORIAL_PHASES.CREATE_CLASSROOM_BUTTON;
    case PROFESSOR_TUTORIAL_PHASES.CREATE_CLASSROOM_BUTTON:
      return PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_CENTER;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_CENTER:
      return PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_NAME;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_NAME:
      return PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_COURSE;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_COURSE:
      return classroomCreated ? PROFESSOR_TUTORIAL_PHASES.STUDENTS_SECTION : PROFESSOR_TUTORIAL_PHASES.WAIT_CLASSROOM_CREATED;
    case PROFESSOR_TUTORIAL_PHASES.WAIT_CLASSROOM_CREATED:
      return classroomCreated ? PROFESSOR_TUTORIAL_PHASES.STUDENTS_SECTION : PROFESSOR_TUTORIAL_PHASES.WAIT_CLASSROOM_CREATED;
    case PROFESSOR_TUTORIAL_PHASES.STUDENTS_SECTION:
      return PROFESSOR_TUTORIAL_PHASES.ADD_STUDENTS;
    case PROFESSOR_TUTORIAL_PHASES.ADD_STUDENTS:
      return importOpen ? PROFESSOR_TUTORIAL_PHASES.CSV_TEMPLATE : PROFESSOR_TUTORIAL_PHASES.ADD_STUDENTS;
    case PROFESSOR_TUTORIAL_PHASES.CSV_TEMPLATE:
      return PROFESSOR_TUTORIAL_PHASES.CSV_UPLOAD;
    default:
      return phase;
  }
}

export function canProfessorTutorialActivatePhase({ phase, pathname, hasTarget, createDialogOpen = false, importDialogOpen = false }) {
  if (!hasTarget) return false;
  switch (getProfessorTutorialVisiblePhase(phase)) {
    case PROFESSOR_TUTORIAL_PHASES.NAV_AULA:
      return true;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_OVERVIEW:
    case PROFESSOR_TUTORIAL_PHASES.CREATE_CLASSROOM_BUTTON:
    case PROFESSOR_TUTORIAL_PHASES.STUDENTS_SECTION:
    case PROFESSOR_TUTORIAL_PHASES.ADD_STUDENTS:
      return pathname === PROFESSOR_TUTORIAL_CLASSROOM_PATH;
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_CENTER:
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_NAME:
    case PROFESSOR_TUTORIAL_PHASES.CLASSROOM_FORM_COURSE:
      return pathname === PROFESSOR_TUTORIAL_CLASSROOM_PATH && createDialogOpen;
    case PROFESSOR_TUTORIAL_PHASES.CSV_TEMPLATE:
    case PROFESSOR_TUTORIAL_PHASES.CSV_UPLOAD:
      return pathname === PROFESSOR_TUTORIAL_CLASSROOM_PATH && importDialogOpen;
    default:
      return false;
  }
}

export function getProfessorTutorialSafeNavigationTarget(phase) {
  if (phase === PROFESSOR_TUTORIAL_PHASES.NAV_AULA) {
    return PROFESSOR_TUTORIAL_CLASSROOM_PATH;
  }
  return null;
}

export function isProfessorTutorialFinalPhase(phase, hasClassrooms) {
  const order = getProfessorTutorialPhaseOrder(hasClassrooms);
  return getProfessorTutorialVisiblePhase(phase) === order[order.length - 1];
}

export function getProfessorTutorialResetState() {
  return {
    phase: PROFESSOR_TUTORIAL_PHASES.NAV_AULA,
    run: false,
    targetReady: false,
    createDialogOpen: false,
    importDialogOpen: false,
    requiresClassroomCreation: false,
  };
}
